/* eslint-disable no-unused-vars */
import { api } from "@/axios";
import { skip } from "@/utils/helpers";
import Vue from "vue";
import { permissionsMiddleWare } from "../../../utils/permissionsMiddleWare";
import { getCache, setCache } from "@/utils/cacheDB";

// Shared in-flight /branch/index request — several components on one page
// dispatch fetchBranch simultaneously; they must not each hit the API
let branchRequest = null;
const state = {
  expenses: [],
  expenseType: [],
  pagination: {},
  details: [],
  expensesReport: [],
  fetchUserBranch: [],
  selectedBranch: []
};

const mutations = {
  set_expenses(state, val) {
    state.expenses = val;
  },
  set_pagination(state, val) {
    state.pagination = val;
  },
  set_expenses_details(state, val) {
    state.details = val;
  },
  set_expense_type(state, val) {
    state.expenseType = val;
  },
  set_expense_report(state, val) {
    state.expensesReport = val;
  },
  set_branch(state, val) {
    state.fetchUserBranch = val;
  },
  set_selectedBranch(state, val) {
    state.selectedBranch = val;
  }
};

const actions = {
  async listExpenses({ commit }, val) {
    try {
      if (permissionsMiddleWare("List Expense")) {
        commit("set_loading", true, { root: true });
        const result = await api.get(
          val
            ? `/template?take=${20}&skip=${skip(val)}&page_no=${val}`
            : "/expense"
        );
        commit("set_expenses", result.data.result.expenses);
        commit("set_pagination", result.data.result.pagination);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : List Expense",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async sortExpanses({ commit }, val) {
    try {
      commit("set_loading", true, { root: true });
      const result = await api.get(
        `/expense?date=${val.date}&branchId=${val.branchId}`
      );
      commit("set_expenses", result.data.result.expenses);
      commit("set_pagination", result.data.result.pagination);
      commit("set_loading", false, { root: true });
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async addExpenses({ dispatch }, payload) {
    await api.post("/expense", payload);
    await dispatch("expenseTypeList");
  },
  async updateExpenses({ dispatch }, payload) {
    if (permissionsMiddleWare("Update Expense")) {
      await api.put(`/expense/${payload.id}`, payload);
      await dispatch("expenseTypeList");
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : Update Expense",
        { duration: 3000 }
      );
    }
  },
  async deleteExpense({ commit, dispatch }, id) {
    try {
      if (permissionsMiddleWare("Delete Expense")) {
        commit("set_loading", true, { root: true });
        await api.delete(`/expense/${id}`);
        dispatch("expenseTypeList");
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Delete Expense",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async expensesDetails({ commit }, { id, payload }) {
    if (permissionsMiddleWare("List Expense Account")) {
      const response = await api.get(
        `/expense/account/${id}?start_date=${payload.start_date}&end_date=${payload.end_date}`,
        {}
      );
      commit("set_expenses_details", response.data.result);
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : List Expense Account",
        { duration: 3000 }
      );
    }
  },
  async expensesDetailsUpdate({ commit, dispatch }, { id, payload }) {
    if (permissionsMiddleWare("Update Expense & Type")) {
      await api.put(`/expense/update-expense/${id}`, payload);
      await dispatch("expensesDetails", id);
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : Update Expense & Type",
        { duration: 3000 }
      );
    }
  },
  async expensesDetailsDelete({ dispatch }, id) {
    if (permissionsMiddleWare("Delete Expense & Type")) {
      await api.delete(`/expense/expense-delete/${id}`);
      await dispatch("expensesDetails", id);
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : Delete Expense & Type",
        { duration: 3000 }
      );
    }
  },
  async expenseTypeList({ commit }, val) {
    try {
      if (permissionsMiddleWare("List Expense Type")) {
        commit("set_loading", true, { root: true });
        const response = await api.get(
          val
            ? `/expense/type?take=${20}&skip=${skip(val)}&page_no=${val}`
            : "/expense/type"
        );
        commit("set_expense_type", response.data.result.expense_type);
        commit("set_pagination", response.data.result.pagination);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : List Expense Type",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async expenseReport({ commit }, payload) {
    try {
      if (permissionsMiddleWare("Expense Report")) {
        commit("set_loading", true, { root: true });
        const response = await api.get("/expense/expense-report", {
          params: { ...payload }
        });
        commit("set_expense_report", response.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Expense Report",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async fetchBranch({ commit }) {
    // Cache-first: branches live in IndexedDB until logout — no API call
    // when the cache is populated
    const cached = await getCache("branches");
    if (cached && cached.length > 0) {
      commit("set_branch", cached);
      return;
    }
    if (!branchRequest) {
      branchRequest = api
        .get("/branch/index")
        .then(async response => {
          const data = response.data.result;
          await setCache("branches", data);
          return data;
        })
        .finally(() => {
          branchRequest = null;
        });
    }
    const data = await branchRequest;
    commit("set_branch", data);
  },
  currentSelectBranch({ commit }, payload) {
    const data = payload;
    commit("set_selectedBranch", data);
  },
  async fetchBrachDetails({ commit }, val) {
    try {
      commit("set_loading", true, { root: true });
      const result = await api.get(
        `/expense/type?date=${val.date}&branchId=${val.branch.key}&take=${val.take}&skip=${val.skip}&page_no=${val.pageNumber}&sort=id`
      );
      commit("set_expense_type", result.data.result.expense_type);
      commit("set_pagination", result.data.result.pagination);

      commit("set_loading", false, { root: true });
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  }
};

const getters = {
  getExpenses: state => state.expenses,
  selectedBranch: state => state.selectedBranch,
  pagination: state => state.pagination,
  detailExpenses: state => state.details,
  expenseType: state => state.expenseType,
  expensesReport: state => state.expensesReport || [],
  fetchUserBranch: state => state.fetchUserBranch
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
